import { isEqual } from "lodash"
import {
  BaseSyntheticEvent,
  Reducer,
  useCallback,
  useMemo,
  useReducer,
} from "react"

type ArrayPropertyNames<T> = {
  [K in keyof T]: T[K] extends Array<unknown> ? K : never
}[keyof T]
type NonArrayPropertyNames<T> = {
  [K in keyof T]: T[K] extends Array<unknown> ? never : K
}[keyof T]
type ArrayElement<T> = T extends Array<infer U> ? U : never

type NonFunctionPropertyNames<T> = {
  [K in keyof T]: T[K] extends Function ? never : K
}[keyof T]
type Primitive = null | undefined | string | number | boolean | symbol | bigint
export type ErrorKey<T> = {
  [K in keyof T]: T[K] extends Array<infer V>
    ? V extends Primitive
      ? `${string & K}.${number}` | K
      :
          | `${string & K}.${number}.${string & NonFunctionPropertyNames<V>}`
          | `${string & K}.${number}`
          | K
    : K
}[keyof T]

// FormErrors represents error messages for the fields in T,
// where T is the type of the stateful form values object.
export type FormErrors<T> = Partial<Record<ErrorKey<T>, string>>

// Internal error type for flexible string operations
type InternalFormErrors = Record<string, string>

// UseFormArgs represents the arguments to be passed to the
// useForm hook.
interface UseFormArgs<T> {
  // defaultValues are the default form values we compare against to
  // determine whether the form has been edited.
  defaultValues: T

  // eq returns whether two form values objects are equal; it is used
  // to determine whether a form has been edited.
  eq(a: T, b: T): boolean

  // validate takes in a form values object and returns error strings
  // by key in the form values object. This is called on submission.
  validate(values: T): FormErrors<T>
}

// UseFormRes represents the response from the useForm hook.
export interface UseFormRes<T> {
  // values is the current state of the form values.
  values: T

  // defaultValues is the current state of the form default values.
  defaultValues: T

  // setValue mutates a non-array field in the form values state.
  setValue<K extends NonArrayPropertyNames<T>>(key: K, value: T[K]): void

  // setValuesToDefault sets the values to the default values.
  setValuesToDefault(): void

  // appendElementToArrayValue adds an element to the end of of an array value in
  // the form values state.
  appendElementToArrayValue<K extends ArrayPropertyNames<T>>(
    key: K,
    element: ArrayElement<T[K]>
  ): void

  // removeFromArrayValue removes an element from the specified index in an array
  // value in the form values state.
  removeElementFromArrayValue<K extends ArrayPropertyNames<T>>(
    key: K,
    index: number
  ): void

  // updateElementInArrayValue updates an element at the specified index in an array
  // value in the form values state.
  updateElementInArrayValue<K extends ArrayPropertyNames<T>>(
    key: K,
    index: number,
    element: ArrayElement<T[K]>
  ): void

  // setDefaultValue updates the defaultValue for a single form value, which we compare against
  // to determine whether the form has been edited.
  setDefaultValue<K extends NonArrayPropertyNames<T>>(key: K, value: T[K]): void

  // setDefaultValues updates the defaultValues, which we compare against
  // to determine whether the form has been edited.
  setDefaultValues(defaultValues: T): void

  // setDefaultValuesToCurrent sets the default values to the current values.
  setDefaultValuesToCurrent(): void

  // handleSubmit takes in an onSubmit callback function to call if the form values
  // pass validation. If the form values fail validation, onSubmit is not called.
  // This returns a FormEventHandler, so that you can easily use this with the form
  // element: <form onSubmit={handleSubmit(onSubmit)} />.
  handleSubmit(onSubmit: (values: T) => void): (e?: BaseSyntheticEvent) => void

  // errors represents the error messages for each field in the form. A key being
  // present in the errors object should signal that there is an error for the
  // relevant field. In other words, valid fields are left out from this object
  // entirely.
  errors: FormErrors<T>

  // setError sets an error message for the provided field in the form values state.
  setError(key: ErrorKey<T>, message: string): void

  // clearErrors clears all errors.
  clearErrors(): void

  // isError represents whether there are any form errors.
  isError: boolean

  // isEdited represents whether the form has been edited (i.e. the current
  // form values are not equal to the default form values).
  isEdited: boolean

  // setArrayValue sets an array value in the form values state
  setArrayValue<K extends ArrayPropertyNames<T>>(key: K, value: T[K]): void
}

// State represents the internal state of the useForm hook.
interface State<T> {
  defaultValues: T
  values: T
  errors: InternalFormErrors
  isEdited: boolean
}

// Action represents the actions that can be dispatched from
// the useForm hook to mutate State.
type Action<
  T,
  K extends ArrayPropertyNames<T> = ArrayPropertyNames<T>,
  L extends NonArrayPropertyNames<T> = NonArrayPropertyNames<T>,
> =
  | {
      type: "setDefaultValues"
      payload: T
    }
  | {
      type: "setValuesToDefault"
    }
  | {
      type: "setNonArrayDefaultValue"
      payload: {
        key: L
        value: T[L]
      }
    }
  | {
      type: "setNonArrayValue"
      payload: {
        key: L
        value: T[L]
      }
    }
  | {
      type: "appendToArrayValue"
      payload: {
        key: K
        element: ArrayElement<T[K]>
      }
    }
  | {
      type: "removeFromArrayValue"
      payload: {
        key: K
        index: number
      }
    }
  | {
      type: "updateInArrayValue"
      payload: {
        key: K
        index: number
        element: ArrayElement<T[K]>
      }
    }
  | {
      type: "setErrors"
      payload: FormErrors<T>
    }
  | {
      type: "partialSetErrors"
      payload: Partial<FormErrors<T>>
    }
  | {
      type: "setArrayValue"
      payload: {
        key: K
        value: T[K]
      }
    }

// reducerWrapper wraps the reducer for the internal state of
// the useForm hook. It takes in eq, a method for checking if
// two form values objects are equal that we use to determine
// whether the form has been edited.
function reducerWrapper<T>(
  eq: (a: T, b: T) => boolean
): Reducer<State<T>, Action<T>> {
  return function reducer(state: State<T>, action: Action<T>): State<T> {
    switch (action.type) {
      case "setDefaultValues": {
        return {
          ...state,
          defaultValues: action.payload,
          values: action.payload,
          isEdited: false,
          errors: {},
        }
      }
      case "setValuesToDefault": {
        return {
          ...state,
          values: { ...state.defaultValues },
          isEdited: false,
        }
      }
      case "setNonArrayDefaultValue": {
        const { key, value } = action.payload

        const defaultValues = {
          ...state.defaultValues,
          [key]: value,
        }

        const values = {
          ...state.values,
          [key]: value,
        }

        return {
          ...state,
          defaultValues,
          values,
          isEdited: !eq(defaultValues, values),
        }
      }
      case "setNonArrayValue": {
        const { key, value } = action.payload

        const values = {
          ...state.values,
          [key]: value,
        }

        const errors = { ...state.errors }
        if ((key as string) in errors) {
          delete errors[key as string]
        }

        return {
          ...state,
          values,
          isEdited: !eq(state.defaultValues, values),
          errors,
        }
      }
      case "appendToArrayValue": {
        const { key, element } = action.payload

        const values = {
          ...state.values,
          [key]: [...((state.values[key] ?? []) as Array<unknown>), element],
        }

        const errors = { ...state.errors }
        if ((key as string) in errors) {
          delete errors[key as string]
        }

        return {
          ...state,
          values,
          isEdited: !eq(state.defaultValues, values),
          errors,
        }
      }
      case "removeFromArrayValue": {
        const { key, index } = action.payload

        const value = [...((state.values[key] ?? []) as Array<unknown>)]
        value.splice(index, 1)
        const values = {
          ...state.values,
          [key]: value,
        }

        const errors = { ...state.errors }
        for (const errorKey of Object.keys(errors)) {
          const [k, i, v] = errorKey.split(".")

          if (k !== key) {
            continue
          }

          if (!i || Number(i) === index) {
            delete errors[errorKey]
            continue
          }

          if (index < Number(i)) {
            errors[`${k}.${Number(i) - 1}${v ? `.${v}` : ""}`] =
              errors[errorKey]
            delete errors[errorKey]
          }
        }

        return {
          ...state,
          values,
          isEdited: !eq(state.defaultValues, values),
          errors,
        }
      }
      case "updateInArrayValue": {
        const { key, index, element } = action.payload

        const value = [...((state.values[key] ?? []) as Array<unknown>)]
        value[index] = element
        const values = {
          ...state.values,
          [key]: value,
        }

        const errors = { ...state.errors }
        for (const errorKey of Object.keys(errors)) {
          const [k, i, v] = errorKey.split(".")

          if (k !== key) {
            continue
          }

          if (!i) {
            delete errors[errorKey]
            continue
          }

          if (
            Number(i) === index &&
            (!v ||
              !isEqual(
                (element as Record<string, unknown>)[v],
                (state.values[key] as Array<Record<string, unknown>>)[
                  Number(i)
                ][v]
              ))
          ) {
            delete errors[errorKey]
          }
        }

        return {
          ...state,
          values,
          isEdited: !eq(state.defaultValues, values),
          errors,
        }
      }
      case "setErrors": {
        return {
          ...state,
          errors: action.payload as InternalFormErrors,
        }
      }
      case "partialSetErrors": {
        const newErrors = { ...state.errors }
        for (const [key, value] of Object.entries(action.payload)) {
          if (typeof value === "string") {
            newErrors[key] = value
          }
        }
        return {
          ...state,
          errors: newErrors,
        }
      }
      case "setArrayValue": {
        const { key, value } = action.payload

        const values = {
          ...state.values,
          [key]: value,
        }

        const errors = { ...state.errors }
        // Clear any errors for this array
        for (const errorKey of Object.keys(errors)) {
          if (errorKey.startsWith(key as string)) {
            delete errors[errorKey]
          }
        }

        return {
          ...state,
          values,
          isEdited: !eq(state.defaultValues, values),
          errors,
        }
      }
      default:
        throw new Error("unexpected action")
    }
  }
}

// hasErrors returns whether there are any errors in the form errors object.
function hasErrors<T>(errors: FormErrors<T>): boolean {
  return Object.values(errors).some((error) => !!error)
}

// useForm is a hook to centralize & simplify building simple forms.
// If your form is complex (e.g. like our form for sales), you likely
// should not use this.
export function useForm<T>({
  defaultValues,
  eq,
  validate,
}: UseFormArgs<T>): UseFormRes<T> {
  const [state, dispatch] = useReducer(reducerWrapper(eq), {
    values: defaultValues,
    defaultValues,
    errors: {},
    isEdited: false,
  })

  const handleSubmit = useCallback(
    (onSubmit: (values: T) => void) => (e?: BaseSyntheticEvent) => {
      e?.preventDefault()

      const errs = validate(state.values)
      dispatch({
        type: "setErrors",
        payload: errs,
      })
      if (hasErrors(errs)) {
        return
      }

      onSubmit(state.values)
    },
    [state.values, validate]
  )

  const setValue = useCallback(
    <K extends NonArrayPropertyNames<T>>(key: K, value: T[K]) => {
      dispatch({
        type: "setNonArrayValue",
        payload: { key, value },
      })
    },
    []
  )

  const setValuesToDefault = useCallback(() => {
    dispatch({
      type: "setValuesToDefault",
    })
  }, [])

  const appendElementToArrayValue = useCallback(
    <K extends ArrayPropertyNames<T>>(key: K, element: ArrayElement<T[K]>) => {
      dispatch({
        type: "appendToArrayValue",
        payload: { key, element },
      })
    },
    []
  )

  const removeElementFromArrayValue = useCallback(
    <K extends ArrayPropertyNames<T>>(key: K, index: number) => {
      dispatch({
        type: "removeFromArrayValue",
        payload: { key, index },
      })
    },
    []
  )

  const updateElementInArrayValue = useCallback(
    <K extends ArrayPropertyNames<T>>(
      key: K,
      index: number,
      element: ArrayElement<T[K]>
    ) => {
      dispatch({
        type: "updateInArrayValue",
        payload: { key, index, element },
      })
    },
    []
  )

  const setDefaultValues = useCallback((newDefaultValues: T) => {
    dispatch({
      type: "setDefaultValues",
      payload: newDefaultValues,
    })
  }, [])

  const setDefaultValue = useCallback(
    <K extends NonArrayPropertyNames<T>>(key: K, value: T[K]) => {
      dispatch({
        type: "setNonArrayDefaultValue",
        payload: { key, value },
      })
    },
    []
  )

  const setDefaultValuesToCurrent = useCallback(() => {
    dispatch({
      type: "setDefaultValues",
      payload: state.values,
    })
  }, [state.values])

  const setError = useCallback((key: ErrorKey<T>, message: string) => {
    const payload: Partial<FormErrors<T>> = {}
    payload[key] = message
    dispatch({
      type: "partialSetErrors",
      payload,
    })
  }, [])

  const clearErrors = useCallback(() => {
    dispatch({ type: "setErrors", payload: {} })
  }, [])

  const isError = useMemo(() => hasErrors(state.errors), [state.errors])

  const setArrayValue = useCallback(
    <K extends ArrayPropertyNames<T>>(key: K, value: T[K]) => {
      dispatch({
        type: "setArrayValue",
        payload: { key, value },
      })
    },
    []
  )

  return {
    values: state.values,
    defaultValues: state.defaultValues,
    errors: state.errors as FormErrors<T>,
    setError,
    clearErrors,
    isError,
    isEdited: state.isEdited,
    setValue,
    setValuesToDefault,
    handleSubmit,
    setDefaultValue,
    setDefaultValues,
    setDefaultValuesToCurrent,
    appendElementToArrayValue,
    removeElementFromArrayValue,
    updateElementInArrayValue,
    setArrayValue,
  }
}
